import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  VirtualViewportEntry,
  VirtualViewportItemContext,
  VirtualViewportTrackBy,
  VirtualViewportVisibleRange,
} from '../virtualization.types';

@Component({
  selector: 'app-virtual-list-viewport',
  templateUrl: './virtual-list-viewport.component.html',
  styleUrls: ['./virtual-list-viewport.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualListViewportComponent<T> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: T[] = [];
  @Input() trackBy: VirtualViewportTrackBy<T> = (index: number) => index;
  @Input() appendDisabled = false;
  /** Render one loading item after the real collection, using its normal template. */
  @Input() appendLoading = false;
  @Input() appendPlaceholder: T | null = null;
  @Input() appendTriggerMarginPx = 1200;
  @Input() estimatedItemHeight = 520;
  @Input() minRenderCount = 6;
  @Input() itemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;
  @Input() scrollTarget: HTMLElement | null = null;
  @Input() gapPx = 0;
  // Pagination may advance without adding visible items (e.g. a client-side filter).
  @Input() appendKey: string | number | null = null;

  @Output() appendRequested = new EventEmitter<void>();
  @Output() visibleRangeChange = new EventEmitter<VirtualViewportVisibleRange>();

  @ContentChild(TemplateRef) projectedItemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;
  @ViewChild('viewportRoot', { static: true }) viewportRoot!: ElementRef<HTMLElement>;
  @ViewChild('appendSentinel', { static: true }) appendSentinel!: ElementRef<HTMLElement>;
  @ViewChildren('renderedItem', { read: ElementRef }) renderedItems?: QueryList<ElementRef<HTMLElement>>;

  public visibleItems: VirtualViewportEntry<T>[] = [];
  public topSpacerHeight = 0;
  public bottomSpacerHeight = 0;

  private readonly isBrowser: boolean;
  private readonly itemHeights = new Map<string, number>();
  private readonly observedItems = new Map<string, HTMLElement>();
  private resizeObserver: ResizeObserver | null = null;
  private appendObserver: IntersectionObserver | null = null;
  private containerResizeObserver: ResizeObserver | null = null;
  private renderedItemsSubscription?: Subscription;
  private boundScrollTarget: HTMLElement | null = null;
  private readonly onTargetScroll = () => this.scheduleViewportUpdate();
  private frameId: number | null = null;
  private frameTimer: ReturnType<typeof setTimeout> | null = null;
  private viewInitialized = false;
  private destroyed = false;
  private lastWidth = 0;
  private pendingScrollAdjustment = 0;
  private requestedScrollTop: number | null = null;
  private offsets: number[] = [0];
  private averageItemHeight = 520;
  private lastRange: VirtualViewportVisibleRange | null = null;
  private lastAppendRequest: string | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get resolvedTemplate(): TemplateRef<VirtualViewportItemContext<T>> | null {
    return this.itemTemplate || this.projectedItemTemplate || null;
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.renderedItemsSubscription = this.renderedItems?.changes.subscribe(() => this.scheduleViewportUpdate());
    this.bindScrollTarget();
    this.scheduleViewportUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['trackBy']) {
      const keys = new Set(this.items.map((item, index) => this.resolveKey(item, index)));
      this.itemHeights.forEach((_, key) => {
        if (!keys.has(key)) this.itemHeights.delete(key);
      });
    }
    if (changes['trackBy']) {
      this.itemHeights.clear();
    }
    if (changes['scrollTarget'] && this.viewInitialized) this.bindScrollTarget();
    if (changes['appendTriggerMarginPx']) {
      this.appendObserver?.disconnect();
      this.appendObserver = null;
    }
    this.averageItemHeight = this.computeAverageHeight();
    this.rebuildOffsets();
    this.scheduleViewportUpdate();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.boundScrollTarget?.removeEventListener('scroll', this.onTargetScroll);
    this.renderedItemsSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.appendObserver?.disconnect();
    this.containerResizeObserver?.disconnect();
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    if (this.frameTimer !== null) clearTimeout(this.frameTimer);
    this.observedItems.clear();
    this.itemHeights.clear();
  }

  /** Recheck a revealed viewport, also allowing an explicit retry of append. */
  public refreshViewport(): void {
    this.lastAppendRequest = null;
    this.scheduleViewportUpdate();
  }

  /** Apply restoration after the spacers have been rendered, rather than clamping to an empty DOM. */
  public scrollToOffset(scrollTop: number): void {
    this.requestedScrollTop = Math.max(0, scrollTop);
    this.pendingScrollAdjustment = 0;
    this.scheduleViewportUpdate();
  }

  public get itemGap(): number {
    return Math.max(0, this.gapPx || 0);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.scrollTarget) this.scheduleViewportUpdate();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleViewportUpdate();
  }

  trackVisibleItem(_: number, entry: VirtualViewportEntry<T>): string {
    return entry.key;
  }

  private scheduleViewportUpdate(): void {
    if (this.destroyed) return;
    if (!this.isBrowser) {
      this.updateViewport();
      return;
    }
    if (!this.viewInitialized || this.frameId !== null || this.frameTimer !== null) {
      return;
    }

    const update = () => {
      this.frameId = null;
      this.frameTimer = null;
      if (this.destroyed || !this.isViewportVisible()) return;
      this.updateViewport();
      // Measurements and the sentinel must refer to this slice, not the previous DOM.
      this.cdr.detectChanges();
      this.applyPendingScroll();
      const heightsChanged = this.syncItemObservers();
      this.syncAppendObserver();
      if (!heightsChanged) this.maybeRequestAppendFromProximity();
    };
    if (typeof requestAnimationFrame === 'function') this.frameId = requestAnimationFrame(update);
    else this.frameTimer = setTimeout(update, 0);
  }

  private bindScrollTarget(): void {
    if (!this.isBrowser) return;
    this.boundScrollTarget?.removeEventListener('scroll', this.onTargetScroll);
    this.boundScrollTarget = this.scrollTarget;
    this.boundScrollTarget?.addEventListener('scroll', this.onTargetScroll, { passive: true });
    this.appendObserver?.disconnect();
    this.appendObserver = null;
    this.containerResizeObserver?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      this.containerResizeObserver = new ResizeObserver(() => {
        if (this.destroyed) return;
        const width = this.viewportRoot.nativeElement.getBoundingClientRect().width;
        if (width > 0 && this.lastWidth > 0 && Math.abs(width - this.lastWidth) >= 1) {
          this.updateMeasurements(() => {
            this.itemHeights.clear();
            return true;
          });
        }
        if (width > 0) this.lastWidth = width;
        this.scheduleViewportUpdate();
      });
      this.containerResizeObserver.observe(this.viewportRoot.nativeElement);
      if (this.scrollTarget) this.containerResizeObserver.observe(this.scrollTarget);
    }
  }

  private updateViewport(): void {
    if (!this.items?.length) {
      this.visibleItems = [];
      this.topSpacerHeight = 0;
      this.bottomSpacerHeight = 0;
      this.emitVisibleRange(0, 0);
      return;
    }

    this.rebuildOffsets();

    if (!this.isBrowser) {
      this.applySlice(0, this.items.length);
      return;
    }

    const bounds = this.getViewportBounds();
    const viewportTop = Math.max(0, bounds.top - this.viewportRoot.nativeElement.getBoundingClientRect().top
      + this.getPendingScrollDelta());
    const viewportBottom = viewportTop + bounds.height;
    const overscan = Math.max(1200, bounds.height * 2);
    const startIndex = this.findStartIndex(Math.max(0, viewportTop - overscan));
    let endIndex = this.findEndIndex(viewportBottom + overscan);

    if ((endIndex - startIndex) < this.minRenderCount) {
      endIndex = Math.min(this.items.length, startIndex + this.minRenderCount);
    }

    this.applySlice(startIndex, endIndex);
  }

  private applySlice(startIndex: number, endIndex: number): void {
    const safeStart = Math.max(0, Math.min(startIndex, this.items.length));
    const safeEnd = Math.max(safeStart, Math.min(endIndex, this.items.length));
    const totalHeight = this.offsets[this.items.length] || 0;

    this.visibleItems = this.items.slice(safeStart, safeEnd).map((item, offset) => ({
      item,
      index: safeStart + offset,
      key: this.resolveKey(item, safeStart + offset),
    }));
    this.topSpacerHeight = this.offsets[safeStart] || 0;
    this.bottomSpacerHeight = Math.max(0, totalHeight - (this.offsets[safeEnd] || 0));
    this.emitVisibleRange(safeStart, safeEnd);
  }

  private emitVisibleRange(start: number, end: number): void {
    if (this.lastRange?.start === start && this.lastRange?.end === end) {
      return;
    }

    this.lastRange = { start, end };
    this.visibleRangeChange.emit(this.lastRange);
  }

  private rebuildOffsets(): void {
    const nextOffsets = new Array(this.items.length + 1);
    nextOffsets[0] = 0;

    for (let index = 0; index < this.items.length; index++) {
      nextOffsets[index + 1] = nextOffsets[index] + this.resolveHeightForIndex(index)
        + (index < this.items.length - 1 ? this.itemGap : 0);
    }

    this.offsets = nextOffsets;
  }

  private resolveHeightForIndex(index: number): number {
    return this.itemHeights.get(this.resolveKey(this.items[index], index)) || this.averageItemHeight;
  }

  private findStartIndex(offset: number): number {
    let low = 0;
    let high = this.items.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.offsets[mid + 1] || 0) <= offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }

  private findEndIndex(offset: number): number {
    let low = 0;
    let high = this.items.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.offsets[mid] || 0) < offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return Math.max(1, Math.min(this.items.length, low));
  }

  private syncItemObservers(): boolean {
    if (!this.isBrowser) return false;

    if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        // Render the current slice before measuring; queued entries can refer to removed items.
        this.scheduleViewportUpdate();
      });
    }

    const nextObserved = new Map<string, HTMLElement>();
    (this.renderedItems?.toArray() || []).forEach((ref) => {
      const element = ref.nativeElement;
      const key = element.dataset['virtualKey'];
      if (!key) {
        return;
      }

      nextObserved.set(key, element);
      if (this.observedItems.get(key) !== element) {
        this.resizeObserver?.observe(element);
      }
    });

    this.observedItems.forEach((element, key) => {
      if (nextObserved.get(key) === element) {
        return;
      }
      this.resizeObserver?.unobserve(element);
    });

    this.observedItems.clear();
    nextObserved.forEach((element, key) => this.observedItems.set(key, element));
    return this.updateMeasurements(() => {
      let changed = false;
      nextObserved.forEach((element, key) => {
        // contentRect omits borders and padding, so always measure the border box.
        changed = this.captureItemHeight(key, element.getBoundingClientRect().height) || changed;
      });
      return changed;
    });
  }

  private updateMeasurements(update: () => boolean): boolean {
    const viewportOffset = this.isBrowser && this.viewportRoot
      ? this.getViewportBounds().top - this.viewportRoot.nativeElement.getBoundingClientRect().top
        + this.getPendingScrollDelta()
      : -1;
    const anchorIndex = this.findStartIndex(Math.max(0, viewportOffset));
    const previousOffset = this.offsets[anchorIndex] || 0;
    if (!update()) return false;
    this.averageItemHeight = this.computeAverageHeight();
    this.rebuildOffsets();
    if (viewportOffset >= 0 && anchorIndex < this.items.length && this.requestedScrollTop === null) {
      this.pendingScrollAdjustment += (this.offsets[anchorIndex] || 0) - previousOffset;
    }
    this.scheduleViewportUpdate();
    return true;
  }

  private captureItemHeight(key: string, nextHeight: number): boolean {
    // A display:none ancestor must not poison the cache with zero-height rows.
    if (nextHeight <= 0) return false;
    const normalizedHeight = Math.max(1, Math.ceil(nextHeight));
    const previousHeight = this.itemHeights.get(key) || 0;

    if (Math.abs(previousHeight - normalizedHeight) < 2) {
      return false;
    }

    this.itemHeights.set(key, normalizedHeight);
    return true;
  }

  private computeAverageHeight(): number {
    const measuredHeights = Array.from(this.itemHeights.values()).filter((height) => height > 0);
    if (!measuredHeights.length) {
      return Math.max(1, Math.round(this.estimatedItemHeight || 520));
    }

    const total = measuredHeights.reduce((sum, height) => sum + height, 0);
    return Math.max(1, Math.round(total / measuredHeights.length));
  }

  private syncAppendObserver(): void {
    if (!this.isBrowser || typeof IntersectionObserver === 'undefined') {
      return;
    }

    if (!this.appendObserver) {
      this.appendObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        this.maybeRequestAppendFromProximity();
      }, {
        root: this.scrollTarget,
        rootMargin: `${this.appendTriggerMarginPx}px 0px ${this.appendTriggerMarginPx}px 0px`,
        threshold: 0,
      });
    }

    this.appendObserver.observe(this.appendSentinel.nativeElement);
  }

  private maybeRequestAppendFromProximity(): void {
    if (!this.isBrowser || this.destroyed || this.appendDisabled || this.appendLoading || !this.isViewportVisible()) {
      return;
    }

    const rect = this.appendSentinel.nativeElement.getBoundingClientRect();
    const bounds = this.getViewportBounds();
    const withinAppendZone = rect.top <= bounds.bottom + this.appendTriggerMarginPx
      && rect.bottom >= bounds.top - this.appendTriggerMarginPx;

    if (!withinAppendZone) {
      return;
    }

    const requestKey = JSON.stringify(this.appendKey === null
      ? [this.items.length, this.items.length ? this.resolveKey(this.items[this.items.length - 1], this.items.length - 1) : null]
      : [this.appendKey]);
    if (this.lastAppendRequest === requestKey) {
      return;
    }

    this.lastAppendRequest = requestKey;
    this.appendRequested.emit();
  }

  private resolveKey(item: T, index: number): string {
    const key = this.trackBy(index, item);
    if (typeof key === 'string' || typeof key === 'number') {
      return `${key}`;
    }

    return `index:${index}`;
  }

  private isViewportVisible(): boolean {
    return this.viewportRoot.nativeElement.getBoundingClientRect().width > 0
      && this.getViewportBounds().height > 0;
  }

  private getViewportBounds(): { top: number; bottom: number; height: number } {
    const windowHeight = this.isBrowser ? window.innerHeight || this.document.documentElement?.clientHeight || 0 : 0;
    const targetTop = this.scrollTarget
      ? this.scrollTarget.getBoundingClientRect().top + this.scrollTarget.clientTop : 0;
    const top = Math.max(0, targetTop);
    const bottom = this.scrollTarget ? Math.min(windowHeight, targetTop + this.scrollTarget.clientHeight) : windowHeight;
    return { top, bottom, height: Math.max(0, bottom - top) };
  }

  private getScrollTop(): number {
    if (this.scrollTarget) return this.scrollTarget.scrollTop;
    return this.isBrowser
      ? window.scrollY || window.pageYOffset || this.document.documentElement?.scrollTop || 0
      : 0;
  }

  private getPendingScrollDelta(): number {
    return this.requestedScrollTop === null ? this.pendingScrollAdjustment : this.requestedScrollTop - this.getScrollTop();
  }

  private applyPendingScroll(): void {
    if (this.requestedScrollTop === null && !this.pendingScrollAdjustment) return;
    const scrollTop = this.requestedScrollTop ?? this.getScrollTop() + this.pendingScrollAdjustment;
    this.requestedScrollTop = null;
    this.pendingScrollAdjustment = 0;
    if (this.scrollTarget) this.scrollTarget.scrollTop = scrollTop;
    else window.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
    // The browser can clamp restoration after a filter or a shorter response.
    this.scheduleViewportUpdate();
  }
}
